import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppointmentResponse } from '@store/generated/appointments';
import { ViewMode, DisplayMode, OrgScheduleFilterState } from '../types';

// 0 = Sunday, 1 = Monday, ..., 6 = Saturday — matches date-fns weekStartsOn
export type WeekStartDay = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const DEFAULT_WEEK_START_DAY: WeekStartDay = 1; // Monday
export const DEFAULT_CALENDAR_START_HOUR = 6; // 6:00 AM
export const DEFAULT_CALENDAR_END_HOUR = 22; // 10:00 PM

// Export AppointmentsState interface for use in store/types.ts
// This now contains ONLY client state - server state is managed by RTK Query
export interface AppointmentsState {
  // View control states (client UI state)
  viewMode: ViewMode;
  displayMode: DisplayMode;
  currentDate: string; // ISO string for serialization
  scrollToNowRequestId: number;

  // Calendar granularity settings (client UI state)
  calendarGranularity: {
    step: 1 | 5 | 10 | 15; // Precision for drag/resize in minutes
    timeslots: 1 | 2 | 3 | 4 | 6 | 12; // Number of slots grouped per visual row
  };

  // First day of the week shown in week view (0=Sun..6=Sat)
  weekStartDay: WeekStartDay;

  // Vertical scroll bounds for the day/week views (hour-of-day, 0-23 for
  // start; 1-24 for end). End is exclusive — setting 22 stops the visible
  // range at 10:00 PM.
  calendarStartHour: number;
  calendarEndHour: number;

  // UI interaction state (client UI state)
  isSidebarOpen: boolean; // Drawer state for mobile
  editingAppointmentId: string | null; // Which appointment is being edited

  // Tutorial mode (client application state)
  isTutorialMode: boolean;
  mockAppointments: AppointmentResponse[]; // Only for tutorial mode

  // Org-level schedule filters (client UI state, not persisted)
  orgScheduleFilters: OrgScheduleFilterState;

  // Transient optimistic appointment patches keyed by appointment_id.
  optimisticAppointmentPatches: Record<string, Partial<AppointmentResponse>>;
}

// Initial state with sensible defaults - only client state
const initialState: AppointmentsState = {
  // View control states - defaults will be overridden by persistence
  viewMode: 'week',
  displayMode: 'calendar',
  currentDate: new Date().toISOString(),
  scrollToNowRequestId: 0,

  // Calendar granularity settings - defaults to hourly rows with 15-min precision
  calendarGranularity: {
    step: 15,
    timeslots: 4, // 4 * 15min = 60min rows
  },

  weekStartDay: DEFAULT_WEEK_START_DAY,
  calendarStartHour: DEFAULT_CALENDAR_START_HOUR,
  calendarEndHour: DEFAULT_CALENDAR_END_HOUR,

  // UI interaction state
  isSidebarOpen: false, // Default closed on mobile, will be overridden on desktop
  editingAppointmentId: null, // No appointment being edited initially

  // Tutorial mode
  isTutorialMode: false,
  mockAppointments: [],

  // Org-level schedule filters (reset each session)
  orgScheduleFilters: {
    isOrgViewActive: false,
    selectedProviderIds: null,
    selectedPatientIds: [],
  },

  optimisticAppointmentPatches: {},
};

export const appointmentsSlice = createSlice({
  name: 'appointments',
  initialState,
  reducers: {
    // View control actions (client UI state)
    setViewMode: (state, action: PayloadAction<ViewMode>) => {
      state.viewMode = action.payload;
    },

    setDisplayMode: (state, action: PayloadAction<DisplayMode>) => {
      state.displayMode = action.payload;
    },

    setCurrentDate: (state, action: PayloadAction<string>) => {
      state.currentDate = action.payload;
    },

    requestScrollToNow: state => {
      state.scrollToNowRequestId += 1;
    },

    setCalendarGranularity: (
      state,
      action: PayloadAction<{ step: 1 | 5 | 10 | 15; timeslots: 1 | 2 | 3 | 4 | 6 | 12 }>,
    ) => {
      // Validate values before setting to prevent invalid states
      const validSteps = [1, 5, 10, 15];
      const validTimeslots = [1, 2, 3, 4, 6, 12];

      if (
        validSteps.includes(action.payload.step) &&
        validTimeslots.includes(action.payload.timeslots)
      ) {
        state.calendarGranularity = action.payload;
      } else {
        // Fallback to safe defaults if invalid
        console.warn('Invalid calendar granularity values, using defaults');
        state.calendarGranularity = { step: 15, timeslots: 4 };
      }
    },

    setWeekStartDay: (state, action: PayloadAction<WeekStartDay>) => {
      if (Number.isInteger(action.payload) && action.payload >= 0 && action.payload <= 6) {
        state.weekStartDay = action.payload;
      } else {
        console.warn('Invalid week start day, using default (Monday)');
        state.weekStartDay = DEFAULT_WEEK_START_DAY;
      }
    },

    setCalendarVisibleHours: (
      state,
      action: PayloadAction<{ startHour: number; endHour: number }>,
    ) => {
      const { startHour, endHour } = action.payload;
      const valid =
        Number.isInteger(startHour) &&
        Number.isInteger(endHour) &&
        startHour >= 0 &&
        startHour <= 23 &&
        endHour >= 1 &&
        endHour <= 24 &&
        endHour > startHour;

      if (valid) {
        state.calendarStartHour = startHour;
        state.calendarEndHour = endHour;
      } else {
        console.warn('Invalid calendar visible hours, using defaults (6 AM - 10 PM)');
        state.calendarStartHour = DEFAULT_CALENDAR_START_HOUR;
        state.calendarEndHour = DEFAULT_CALENDAR_END_HOUR;
      }
    },

    // UI interaction actions (client UI state)
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.isSidebarOpen = action.payload;
    },

    setEditingAppointmentId: (state, action: PayloadAction<string | null>) => {
      state.editingAppointmentId = action.payload;
    },

    // Tutorial mode actions (client application state)
    setTutorialMode: (state, action: PayloadAction<boolean>) => {
      state.isTutorialMode = action.payload;
      if (!action.payload) {
        // Clear mock data when exiting tutorial mode
        state.mockAppointments = [];
      }
    },

    setMockAppointments: (state, action: PayloadAction<AppointmentResponse[]>) => {
      state.mockAppointments = action.payload;
    },

    // Org schedule filter actions (client UI state)
    setOrgViewActive: (state, action: PayloadAction<boolean>) => {
      state.orgScheduleFilters.isOrgViewActive = action.payload;
      if (!action.payload) {
        // Clear filters when switching back to personal view
        state.orgScheduleFilters.selectedProviderIds = null;
        state.orgScheduleFilters.selectedPatientIds = [];
      }
    },

    setSelectedProviderIds: (state, action: PayloadAction<string[] | null>) => {
      state.orgScheduleFilters.selectedProviderIds = action.payload;
    },

    setSelectedPatientIds: (state, action: PayloadAction<string[]>) => {
      state.orgScheduleFilters.selectedPatientIds = action.payload;
    },

    resetOrgScheduleFilters: state => {
      state.orgScheduleFilters = initialState.orgScheduleFilters;
    },

    setOptimisticAppointmentPatch: (
      state,
      action: PayloadAction<{
        appointmentId: string;
        patch: Partial<AppointmentResponse>;
      }>,
    ) => {
      const existing = state.optimisticAppointmentPatches[action.payload.appointmentId] ?? {};
      state.optimisticAppointmentPatches[action.payload.appointmentId] = {
        ...existing,
        ...action.payload.patch,
        block_state:
          existing.block_state && action.payload.patch.block_state
            ? {
                ...existing.block_state,
                ...action.payload.patch.block_state,
                qualifiers: {
                  ...existing.block_state.qualifiers,
                  ...action.payload.patch.block_state.qualifiers,
                },
              }
            : (action.payload.patch.block_state ?? existing.block_state),
      };
    },

    clearOptimisticAppointmentPatch: (state, action: PayloadAction<string>) => {
      delete state.optimisticAppointmentPatches[action.payload];
    },

    clearAllOptimisticAppointmentPatches: state => {
      state.optimisticAppointmentPatches = {};
    },

    // Reset actions
    resetAppointmentsState: () => initialState,
  },
});

// Export actions - only client state actions
export const {
  // View control actions
  setViewMode,
  setDisplayMode,
  setCurrentDate,
  requestScrollToNow,
  setCalendarGranularity,
  setWeekStartDay,
  setCalendarVisibleHours,

  // UI interaction actions
  setSidebarOpen,
  setEditingAppointmentId,

  // Tutorial mode actions
  setTutorialMode,
  setMockAppointments,

  // Org schedule filter actions
  setOrgViewActive,
  setSelectedProviderIds,
  setSelectedPatientIds,
  resetOrgScheduleFilters,

  setOptimisticAppointmentPatch,
  clearOptimisticAppointmentPatch,
  clearAllOptimisticAppointmentPatches,

  // Reset actions
  resetAppointmentsState,
} = appointmentsSlice.actions;

// Export the reducer
export default appointmentsSlice.reducer;
